import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getEnabledProducts } from '../data/products'
import { isPageSeoEnabled } from '../config/siteConfig'
import SeoHead from '../components/SeoHead'
import './Products.css'

const Products = () => {
  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.get('search') || ''
  
  // Check if SEO is enabled for this page
  const seoEnabled = isPageSeoEnabled('products')
  
  // Get only enabled products
  const enabledProducts = useMemo(() => getEnabledProducts(), [])

  const filteredProducts = useMemo(() => {
    if (!searchQuery) return enabledProducts

    const query = searchQuery.toLowerCase()
    return enabledProducts.filter(p => 
      p.name.toLowerCase().includes(query) ||
      p.shortDescription.toLowerCase().includes(query) ||
      p.tagline.toLowerCase().includes(query)
    )
  }, [searchQuery, enabledProducts])

  return (
    <div className="products-page">
      <SeoHead
        title="Pharmaceutical Products | SIBR Lifescience Product Portfolio"
        description="Explore SIBR Lifescience's comprehensive range of GMP-certified pharmaceutical products including syrups, tablets, capsules and more."
        canonical="/products"
        seoEnabled={seoEnabled}
      />
      
      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <h1>SIBR Lifescience Product Portfolio</h1>
          <p>
            Explore our comprehensive range of pharmaceutical products designed 
            to address various healthcare needs with proven efficacy and safety.
          </p>
        </div>
      </section>

      {/* Products Listing */}
      <section className="products-listing section">
        <div className="container">
          {/* Search Results Info */}
          {searchQuery && (
            <div className="search-info">
              <p>
                Showing results for "<strong>{searchQuery}</strong>" 
                ({filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''})
              </p>
            </div>
          )}

          {/* Products Grid */}
          {filteredProducts.length > 0 ? (
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <article key={product.id} className="product-card">
                  <div className="product-image">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={`${product.name} - SIBR Lifescience pharmaceutical product`}
                        className="product-img"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="product-placeholder" style={{ display: product.image ? 'none' : 'flex' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="product-content">
                    <h2 className="product-name">{product.name}</h2>
                    <p className="product-tagline">{product.tagline}</p>
                    <p className="product-description">{product.shortDescription}</p>
                    <div className="product-actions">
                      <Link to={`/products/${product.id}`} className="btn btn-primary btn-sm">
                        View Details
                      </Link>
                      {product.link1mg && (
                        <a 
                          href={product.link1mg}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                        >
                          Find on 1mg
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <h3>No products found</h3>
              <p>Try adjusting your search criteria.</p>
              <Link to="/products" className="btn btn-primary">
                View All Products
              </Link>
            </div>
          )}

          {/* 1mg CTA */}
          <div className="external-cta">
            <div className="external-cta-content">
              <h3>Looking for More Information?</h3>
              <p>Find our complete product catalog and purchase options on 1mg.</p>
              <a 
                href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Visit Our 1mg Store
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="disclaimer-banner">
        <div className="container">
          <p>
            <strong>Important:</strong> The information provided on this website is for 
            informational purposes only and is not intended as medical advice. Please consult 
            a qualified healthcare professional before using any medication.
            <Link to="/disclaimer"> Read Full Disclaimer</Link>
          </p>
        </div>
      </section>
    </div>
  )
}

export default Products

